import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  ListTeamResponse,
  PendingInvitation,
  TeamInviteInput,
  TeamMember,
  UpdateTeamMemberInput,
} from "@cura-studio/shared";

// Tenant-scoped team management — CLIENT_ADMIN surface. Wire spec lives in
// docs/TEAM_API.md; Zod types in shared/src/schemas/user.ts. The caller's
// tenantId is derived server-side from session (or x-active-tenant when a
// STUDIO_ADMIN has scoped in), so no tenantId travels on the wire.

const TEAM_BASE = "/api/v1/tenant/users";
const TEAM_KEY = ["team"] as const;

export function useTeam() {
  return useQuery<ListTeamResponse>({
    queryKey: TEAM_KEY,
    queryFn: () => api<ListTeamResponse>(TEAM_BASE),
  });
}

export type InviteTeamMemberResponse = {
  invitation: PendingInvitation;
  // Placeholder magic link — surfaced in the dialog so admins can copy/paste
  // until Postmark DKIM unblocks (Phase 4.5).
  magicLink: string;
};

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation<InviteTeamMemberResponse, Error, TeamInviteInput>({
    mutationFn: (body) =>
      api<InviteTeamMemberResponse>(TEAM_BASE, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}

export type UpdateTeamMemberArgs = {
  userId: string;
  patch: UpdateTeamMemberInput;
};

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation<TeamMember, Error, UpdateTeamMemberArgs>({
    mutationFn: ({ userId, patch }) =>
      api<TeamMember>(`${TEAM_BASE}/${userId}`, {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (invitationId) =>
      api<void>(`${TEAM_BASE}/invitations/${invitationId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}

export type ResendInvitationResponse = {
  invitation: PendingInvitation;
  magicLink: string;
};

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation<ResendInvitationResponse, Error, string>({
    mutationFn: (invitationId) =>
      api<ResendInvitationResponse>(
        `${TEAM_BASE}/invitations/${invitationId}/resend`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEAM_KEY });
    },
  });
}
